/**
 * Shared data for the "Calendar:" row on the Details step.
 *
 * Owns the shared-calendars fetch (via `useSharedCalendarsQuery`) and
 * derives everything both the view (`CalendarField`) and the edit
 * `Select` need: summary name, "(You)" suffix, select options,
 * edit-pencil enablement, and loading state.
 */
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useMailBox } from '@/hooks/use-mailbox';
import { useSharedCalendarsQuery } from '@/api/sharedCalendars';
import { useKFlags } from '@/hooks';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import type { SelectOption } from '@kula-ai/components';
import { CALENDAR_PROVIDERS } from '@/ui-library/components/constants';
import type { ApiReference } from '../../../config/types';
export type { SharedCalendar as SharedCal } from '@/api/sharedCalendars';
import type { SharedCalendar as SharedCal } from '@/api/sharedCalendars';

interface ScheduledCalendar {
  calendar_name?: string;
  provider_calendar_id?: string;
  is_primary?: boolean;
  user_id?: number;
  user_name?: string;
}

export interface CalendarInfoValue {
  provider_calendar_id?: string;
  is_primary?: boolean;
}

interface UseScheduleCalendarDataOptions {
  interviewPlan?: Record<string, unknown>;
  apiReference?: ApiReference;
  /** Live form value of `calendar_info` — drives summary + suffix. */
  calendarInfo?: CalendarInfoValue | null;
  /** Adhoc create has no `interviewPlan` to read
   *  `shared_calendar_enabled` from, so the row used to stay hidden.
   *  Adhoc IS the place where the recruiter implicitly opts into a
   *  shared calendar by picking one — mirror the InterviewActivity
   *  schema which drops the plan-flag gate for the same reason. */
  isAdhoc?: boolean;
  /** Self-schedule edit is the only edit context where legacy allowed
   *  swapping the calendar (`SchCalendarSchedulingFields:365`). Manual
   *  edit kept the saved calendar locked. Pass `true` for any
   *  self-schedule mode so we mirror that gate. */
  isSelfSchedule?: boolean;
}

export interface ScheduleCalendarData {
  /** Whole row should be in the schema at all. */
  shouldShowRow: boolean;

  /** Calendar service is connected in the session. */
  isCalConnected: boolean;

  /** Shared-calendar poll is resolving for the first time. */
  isLoading: boolean;

  /** Raw list of non-deleted shared calendars. */
  calendars: SharedCal[];

  /** Options feed for the edit-mode native `type: 'select'`. */
  selectOptions: SelectOption[];

  /** Name shown in view mode. Empty string when nothing resolves. */
  summaryName: string;

  /** Avatar URL for the calendar owner. Resolved when the owner is the
   *  current user (only avatar we have locally); otherwise undefined and
   *  the consumer falls back to initials via `summaryName`. */
  summaryAvatarUrl?: string;

  /** Append " (You)" in view mode. */
  showYouSuffix: boolean;

  /** Hide the edit pencil entirely. */
  hideEdit: boolean;

  /** Disabled-state tooltip on the edit pencil. */
  disabledTooltip?: string;

  /** Default calendar to seed the form with on create. */
  defaultCalendar?: SharedCal;

  /** Lookup helper used by the Select's onChange transform. */
  findById: (_id: string) => SharedCal | undefined;

  /** Forces a re-poll — call before opening the edit dropdown. */
  refetchUuid: () => void;

  /** True when the recruiter's connected calendar is Outlook (`mscal`).
   *  Drives the Outlook-only "organizer" hint + interviewer-chip warning. */
  isOutlook: boolean;

  /** Owner of the currently-selected (or hydrated) shared calendar,
   *  or `null` when:
   *    - the user's calendar isn't Outlook, OR
   *    - the selected calendar is the recruiter's primary (they own
   *      the event themselves; no hint needed).
   *  Mirrors legacy `calendarOwnerUserId` + name-resolution priority
   *  in `useCalendarSchedulingFields`. */
  outlookOwner: { userId: number | null; name: string } | null;
}

export function useScheduleCalendarData({
  interviewPlan,
  apiReference,
  calendarInfo,
  isAdhoc = false,
  isSelfSchedule = false,
}: UseScheduleCalendarDataOptions): ScheduleCalendarData {
  const isEdit = apiReference === 'edit';
  // Adhoc has no plan to read the opt-in from — treat as enabled so the
  // calendar row renders. Plan-driven flows still honor the plan flag,
  // edit mode honors whatever the existing interview was created with.
  const sharedEnabled =
    isAdhoc || Boolean(interviewPlan?.shared_calendar_enabled);

  const { feSchedulingPreference } = useKFlags() as {
    feSchedulingPreference?: boolean;
  };
  const currUser = useSelector(selectCurrUser) as
    | {
        id?: number;
        avatar_url?: string;
        avatar?: string;
        connected_calendar?: { gcal?: boolean; mscal?: boolean };
      }
    | null
    | undefined;
  const hasConnectedCalendar = Boolean(
    currUser?.connected_calendar?.gcal || currUser?.connected_calendar?.mscal
  );
  const currUserAvatar = currUser?.avatar_url ?? currUser?.avatar;
  const isSharedCalendarEnabled = isEdit || sharedEnabled;
  const shouldShowRow = Boolean(
    feSchedulingPreference && hasConnectedCalendar && isSharedCalendarEnabled
  );

  const { isCalConnected, connectedCalValue } = useMailBox() as {
    isCalConnected: boolean;
    connectedCalValue?: string;
  };
  const isOutlook = connectedCalValue === CALENDAR_PROVIDERS.MICROSOFT;

  const {
    data: calendarData,
    isLoading,
    refetch,
  } = useSharedCalendarsQuery({
    type: 'saved',
    enabled: shouldShowRow && isCalConnected,
  });

  const refetchUuid = useCallback(() => {
    refetch();
  }, [refetch]);

  const calendars: SharedCal[] = useMemo(
    () =>
      (calendarData ?? []).filter((c) => c?.status !== 'deleted_from_provider'),
    [calendarData]
  );

  const findById = useCallback(
    (id: string) => calendars.find((c) => c.provider_calendar_id === id),
    [calendars]
  );

  // Default-calendar seed differs by entry point:
  //   - Adhoc: workspace's `is_default` shared calendar wins (the one
  //     the admin pinned in settings); fall back to the user's
  //     `is_primary` personal calendar only when none is configured.
  //   - Plan-driven: the plan hydrates `calendar_info` upstream
  //     (`fromPlan.ts`). The fallback when the plan has none or the
  //     scheduler lacks access is the recruiter's PERSONAL calendar,
  //     not the workspace shared default — product preference, so
  //     plan authors who didn't pick a calendar stay on their own.
  const defaultCalendar = useMemo(() => {
    if (isAdhoc) {
      return (
        calendars.find((c) => c.is_default) ??
        calendars.find((c) => c.is_primary)
      );
    }
    return calendars.find((c) => c.is_primary);
  }, [calendars, isAdhoc]);

  const scheduledCal = interviewPlan?.scheduled_calendar as
    | ScheduledCalendar
    | undefined;

  // Summary for the view strip. Priority:
  //   1. Currently-selected calendar (from fetched list)
  //   2. Edit mode's `scheduled_calendar.calendar_name` (no extra fetch)
  //   3. Create mode's default calendar name
  //   4. Empty — the row shows just "Calendar:" while loading so we
  //      never pretend a fake "Organizer calendar" exists.
  const summaryName = useMemo(() => {
    const id = calendarInfo?.provider_calendar_id;
    if (id) {
      const selected = findById(id);
      if (selected?.name) return selected.name;
    }
    if (isEdit && scheduledCal?.calendar_name) {
      return scheduledCal.calendar_name;
    }
    if (!isEdit && defaultCalendar?.name) return defaultCalendar.name;
    return '';
  }, [
    calendarInfo?.provider_calendar_id,
    defaultCalendar,
    findById,
    isEdit,
    scheduledCal?.calendar_name,
  ]);

  const showYouSuffix = useMemo(() => {
    const id = calendarInfo?.provider_calendar_id;
    const cal = id ? findById(id) : undefined;
    if (cal) return Boolean(cal.is_primary);
    if (isEdit) return Boolean(scheduledCal?.is_primary);
    if (!isEdit && defaultCalendar) return Boolean(defaultCalendar.is_primary);
    return false;
  }, [
    calendarInfo?.provider_calendar_id,
    defaultCalendar,
    findById,
    isEdit,
    scheduledCal?.is_primary,
  ]);

  // Avatar shown in the read-only Calendar row. We have currUser's
  // photo locally; for other owners the BE doesn't return an avatar
  // URL on `SharedCalendar`, so the kula `<Avatar>` falls back to
  // initials derived from `summaryName`.
  const summaryAvatarUrl = useMemo(() => {
    const id = calendarInfo?.provider_calendar_id;
    const liveCal = id ? findById(id) : undefined;
    const ownerUserId =
      liveCal?.user_id ??
      (isEdit ? scheduledCal?.user_id : undefined) ??
      defaultCalendar?.user_id;
    const isPrimaryCal =
      liveCal?.is_primary ??
      (isEdit ? scheduledCal?.is_primary : undefined) ??
      defaultCalendar?.is_primary ??
      false;
    if (isPrimaryCal) return currUserAvatar;
    if (
      ownerUserId !== undefined &&
      currUser?.id !== undefined &&
      Number(ownerUserId) === Number(currUser.id)
    ) {
      return currUserAvatar;
    }
    return undefined;
  }, [
    calendarInfo?.provider_calendar_id,
    currUser?.id,
    currUserAvatar,
    defaultCalendar,
    findById,
    isEdit,
    scheduledCal?.is_primary,
    scheduledCal?.user_id,
  ]);

  // Legacy parity: manual edit locks the saved calendar; only
  // self-schedule edit allows swapping (and only if the plan has
  // shared_calendar_enabled). Source: `useCalendarSchedulingFields`
  // line 365 — `(isSelfSchedule || !isEdit) && (isEdit ? shared_calendar_enabled : true)`.
  const hideEdit = isEdit && (!isSelfSchedule || !sharedEnabled);
  const onlyPrimaryAvailable =
    calendars.length === 0 ||
    (calendars.length === 1 && calendars[0]?.is_primary);
  const disabledTooltip =
    !isLoading && onlyPrimaryAvailable
      ? "You don't have access to any shared calendars"
      : undefined;

  // Outlook "this calendar is owned by X" derivation — mirrors legacy
  // `useCalendarSchedulingFields.getCalendarOwnerUserId` priority chain.
  // Returns null when the user isn't on Outlook OR when the selected
  // calendar is the recruiter's primary (they're the organizer, so the
  // hint doesn't apply).
  const outlookOwner = useMemo<{
    userId: number | null;
    name: string;
  } | null>(() => {
    if (!isOutlook) return null;
    const id = calendarInfo?.provider_calendar_id;
    const liveCalendar = id ? findById(id) : undefined;
    // Skip when the picked (or hydrated) calendar is the recruiter's own.
    const isLivePrimary = liveCalendar
      ? Boolean(liveCalendar.is_primary)
      : isEdit
        ? Boolean(scheduledCal?.is_primary)
        : Boolean(defaultCalendar?.is_primary);
    if (isLivePrimary) return null;

    // Owner user id — match legacy priority:
    //   1. live calendar (selected via the picker)
    //   2. hydrated `scheduled_calendar` (edit mode)
    //   3. default calendar (create mode pre-pick)
    //   4. fallback: `scheduled_by.id` (edit mode, calendar dropped out
    //      of the user's accessible list).
    const userId =
      liveCalendar?.user_id ??
      (isEdit ? scheduledCal?.user_id : undefined) ??
      defaultCalendar?.user_id ??
      (isEdit
        ? (interviewPlan?.scheduled_by as { id?: number } | undefined)?.id
        : undefined) ??
      null;

    // Owner display name — same priority chain. BE provides `user_name`
    // alongside `user_id` so we don't need to look up the users list
    // separately (legacy did this with a `users` prop that the new
    // wizard doesn't thread).
    const name =
      liveCalendar?.user_name ||
      (isEdit ? scheduledCal?.user_name : undefined) ||
      defaultCalendar?.user_name ||
      (isEdit
        ? (interviewPlan?.scheduled_by as { name?: string } | undefined)?.name
        : undefined) ||
      'the calendar owner';

    return { userId, name };
  }, [
    calendarInfo?.provider_calendar_id,
    defaultCalendar,
    findById,
    interviewPlan,
    isEdit,
    isOutlook,
    scheduledCal,
  ]);

  const selectOptions: SelectOption[] = useMemo(
    () =>
      calendars
        .filter(
          (c): c is SharedCal & { provider_calendar_id: string } =>
            typeof c.provider_calendar_id === 'string'
        )
        .map((c) => ({
          value: c.provider_calendar_id,
          label: `${c.name ?? c.provider_calendar_id}${c.is_primary ? ' (You)' : ''}`,
        })),
    [calendars]
  );

  return {
    shouldShowRow,
    isCalConnected,
    isLoading,
    calendars,
    selectOptions,
    summaryName,
    summaryAvatarUrl,
    showYouSuffix,
    hideEdit,
    disabledTooltip,
    defaultCalendar,
    findById,
    refetchUuid,
    isOutlook,
    outlookOwner,
  };
}
